/**
 * Formatting helpers for the invoice analytics block returned by
 * `POST /api/v1/reports` (`response.analytics`). Shared by the on-screen
 * panel and the PDF tables so both show the same figures.
 * @module invoiceAnalyticsFormatters
 */

const currencyFormatter = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
});

const preciseCurrencyFormatter = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
  minimumFractionDigits: 3,
  maximumFractionDigits: 3,
});

const numberFormatter = new Intl.NumberFormat("en-US", {
  maximumFractionDigits: 2,
});

/**
 * @param {number} value
 * @returns {string} e.g. "$1,234.50"
 */
export const formatMoney = (value) =>
  currencyFormatter.format(Number(value) || 0);

/**
 * Per-mile figures keep three decimals ("$2.944").
 * @param {number} value
 * @returns {string}
 */
export const formatPerMile = (value) =>
  preciseCurrencyFormatter.format(Number(value) || 0);

/**
 * @param {number} value
 * @returns {string} e.g. "49.1%"
 */
export const formatPercent = (value) => `${Number(value) || 0}%`;

/**
 * @param {number} value
 * @returns {string} e.g. "1,234.5"
 */
export const formatNumber = (value) => numberFormatter.format(Number(value) || 0);

/**
 * "2026-03" -> "Mar 2026". Unknown values are returned unchanged.
 * @param {string} month
 * @returns {string}
 */
export const formatMonth = (month) => {
  const match = /^(\d{4})-(\d{2})$/.exec(month || "");
  if (!match) return month || "Unknown";
  const date = new Date(Date.UTC(Number(match[1]), Number(match[2]) - 1, 1));
  return date.toLocaleDateString("en-US", {
    month: "short",
    year: "numeric",
    timeZone: "UTC",
  });
};

/**
 * Average fuel rate is stored as a fraction (0.25) on jobs; show it as a percent.
 * @param {number} rate
 * @returns {string}
 */
export const formatFuelRate = (rate) =>
  `${Number(((Number(rate) || 0) * 100).toFixed(1))}%`;

/**
 * Groups the analytics summary into labelled sections of `{ label, value }` rows.
 * Missing sections are skipped so older responses still render.
 *
 * @param {object} analytics - `response.analytics` from the reports API
 * @returns {Array<{ id: string, title: string, rows: Array<{ label: string, value: string }> }>}
 */
export const buildAnalyticsSections = (analytics) => {
  const summary = analytics?.summary;
  if (!summary) return [];

  const sections = [
    {
      id: "collections",
      title: "Collections",
      rows: [
        { label: "Invoices", value: formatNumber(summary.invoiceCount) },
        { label: "Gross", value: formatMoney(summary.gross) },
        { label: "Paid", value: formatMoney(summary.paid) },
        { label: "Outstanding", value: formatMoney(summary.outstanding) },
        { label: "Overdue", value: formatMoney(summary.overdue) },
        { label: "Collection rate", value: formatPercent(summary.collectionRate) },
        { label: "Average invoice", value: formatMoney(summary.averageInvoice) },
        {
          label: "Average days overdue",
          value: formatNumber(summary.averageDaysOverdue),
        },
      ],
    },
  ];

  if (summary.charges) {
    sections.push({
      id: "charges",
      title: "Charge mix",
      rows: [
        { label: "Base", value: formatMoney(summary.charges.base) },
        { label: "Fuel", value: formatMoney(summary.charges.fuel) },
        { label: "Drop", value: formatMoney(summary.charges.drop) },
        { label: "Other / accessories", value: formatMoney(summary.charges.other) },
      ],
    });
  }

  if (summary.fuel) {
    const fuel = summary.fuel;
    sections.push({
      id: "fuel",
      title: "Fuel surcharge",
      rows: [
        { label: "Total fuel", value: formatMoney(fuel.total) },
        { label: "% of base", value: formatPercent(fuel.percentOfBase) },
        { label: "% of gross", value: formatPercent(fuel.percentOfGross) },
        { label: "Avg per invoice", value: formatMoney(fuel.averagePerInvoice) },
        { label: "Avg per job", value: formatMoney(fuel.averagePerJob) },
        { label: "Avg per leg", value: formatMoney(fuel.averagePerLeg) },
        { label: "Avg per mile", value: formatPerMile(fuel.averagePerMile) },
        { label: "Avg job fuel rate", value: formatFuelRate(fuel.averageFuelRate) },
      ],
    });
  }

  if (summary.operations) {
    const ops = summary.operations;
    sections.push({
      id: "operations",
      title: "Operations",
      rows: [
        { label: "Jobs", value: formatNumber(summary.jobCount) },
        { label: "Legs", value: formatNumber(summary.legCount) },
        { label: "Billed miles", value: formatNumber(ops.miles) },
        { label: "Revenue per mile", value: formatPerMile(ops.revenuePerMile) },
        { label: "Base per mile", value: formatPerMile(ops.basePerMile) },
        { label: "Avg miles per leg", value: formatNumber(ops.averageMilesPerLeg) },
        { label: "Avg revenue per job", value: formatMoney(ops.averageRevenuePerJob) },
        { label: "Avg revenue per leg", value: formatMoney(ops.averageRevenuePerLeg) },
        { label: "Avg jobs per invoice", value: formatNumber(ops.averageJobsPerInvoice) },
        { label: "Avg legs per job", value: formatNumber(ops.averageLegsPerJob) },
        { label: "Avg base rate", value: formatMoney(ops.averageBaseRate) },
        { label: "Total weight", value: formatNumber(ops.weight) },
        { label: "Standby hours", value: formatNumber(ops.standbyHours) },
      ],
    });
  }

  return sections;
};

export default buildAnalyticsSections;
